import { injectable } from "@launchtray/tsyringe-async";
import { PieceColor } from "../../../enums/PieceColor";
import { TriangleRotationStatus } from "../../../enums/TriangleRotationStatus";
import { FigureData } from "../../../models/figure/FigureData";
import { IFigureModel } from "../../../models/figure/IFigureModel";
import { FigureModel } from "../../../models/figure/impl/FigureModel";
import { ISquareModel } from "../../../models/square/ISquareModel";
import { ISquareModelFactory } from "../../squares/ISquareModelFactory";
import { IFigureModelFactory } from "../IFigureModelFactory";
import { FigureUltimateStatus } from "../../../enums/FigureUltimateStatus";
import { SquareModelFigureStatus } from "../../../enums/SquareModelFigureStatus";

@injectable()
export class FigureModelFactory implements IFigureModelFactory {
    private readonly possibleColors: Array<PieceColor>;
    private readonly figureShapes: Array<FigureData>;
    private readonly possible1x1Length: number;
    private readonly possible1x2Length: number;
    private readonly possible2x1Length: number;
    private readonly possible2x2Length: number;
    private readonly initialUltimatePercentage: number;
    public constructor(
        private readonly emptySquareModelFactory: ISquareModelFactory,
        private readonly halfSquareModelFactory: ISquareModelFactory,
        private readonly fullSquareModelFactory: ISquareModelFactory
    ) {
        this.possibleColors = new Array<PieceColor>(
            PieceColor.BLUE, PieceColor.GREEN, PieceColor.ORANGE,
            PieceColor.PURPLE, PieceColor.RED, PieceColor.YELLOW
        );
        this.figureShapes = new Array<FigureData>(
            new FigureData(1, 1),
            new FigureData(1, 2),
            new FigureData(2, 1),
            new FigureData(2, 2)
        );
        this.possible1x1Length = 5;
        this.possible2x1Length = 10;
        this.possible1x2Length = 10;
        this.possible2x2Length = 15;
        this.initialUltimatePercentage = 0;
    }

    public createSpecialSlotFigure(): IFigureModel {
        const shape = this.figureShapes[0];
        const rndColor = this.possibleColors.random();
        if (Array.isArray(rndColor)) {
            console.warn("FigureModelFactory.createSpecialSlotFigure generate array!");
            return;
        }
        const ultimateStatus = FigureUltimateStatus.ULTIMATE;
        return new FigureModel(shape, this.fullSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, rndColor), ultimateStatus);
    }

    public createRandomFigure(): IFigureModel {
        const figureDataIndex = Math.round(Math.random() * (this.figureShapes.length - 1));
        const rndColor = this.possibleColors.random();
        if (Array.isArray(rndColor)) {
            console.warn("FigureModelFactory.createRandomFigure generate array!");
            return;
        }
        const rndUltimateStatus = Math.round(Math.random() * 100) < this.initialUltimatePercentage ? FigureUltimateStatus.ULTIMATE : FigureUltimateStatus.NONE;
        switch (this.figureShapes[figureDataIndex].height * this.figureShapes[figureDataIndex].width) {
            case 1:
                const ultimatePercentage = 0;
                const shape = this.takeAndColorizePossible1x1(rndColor);
                const status = shape.FigureStatus == SquareModelFigureStatus.FULL && Math.round(Math.random() * 100) < ultimatePercentage ? FigureUltimateStatus.ULTIMATE : FigureUltimateStatus.NONE;
                return new FigureModel(this.figureShapes[figureDataIndex], shape, status);
            case 2:
                if (this.figureShapes[figureDataIndex].height > this.figureShapes[figureDataIndex].width){
                    return new FigureModel(this.figureShapes[figureDataIndex], this.takeAndColorizePossible2x1(rndColor), rndUltimateStatus); 
                } else {
                    return new FigureModel(this.figureShapes[figureDataIndex], this.takeAndColorizePossible1x2(rndColor), rndUltimateStatus);
                }
            case 4:
                return new FigureModel(this.figureShapes[figureDataIndex], this.takeAndColorizePossible2x2(rndColor), FigureUltimateStatus.NONE);
            default:
                throw new Error("Unexpected WxH");
        }
    }

    private takeAndColorizePossible1x1(color: PieceColor): ISquareModel {
        const rnd = Math.round(Math.random() * (this.possible1x1Length - 1));
        switch (rnd) {
            case 0: return this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color);
            case 1: return this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color);
            case 2: return this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color);
            case 3: return this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color);
            case 4: return this.fullSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color);
            default: throw new Error(`${this} not implemented case ${rnd}`);
        }
    }

    private takeAndColorizePossible2x1(color: PieceColor): Array<ISquareModel> {
        const rnd = Math.round(Math.random() * (this.possible2x1Length - 1));
        switch (rnd) {
            case 0:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color)
                );
            case 1:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 2:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color)
                );
            case 3:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color)
                );
            case 4:
                return new Array<ISquareModel>(
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 5:
                return new Array<ISquareModel>(
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color)
                )
            case 6:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color)
                );
            case 7:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 8:
                return new Array<ISquareModel>(
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color)
                );
            case 9:
                return new Array<ISquareModel>(
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color),
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            default: throw new Error(`${this} not implemented case ${rnd}`);
        }
    }

    private takeAndColorizePossible1x2(color: PieceColor): Array<ISquareModel> {
        const rnd = Math.round(Math.random() * (this.possible1x2Length - 1));
        switch (rnd) {
            case 0:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color)
                );
            case 1:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 2:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 3:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color)
                );
            case 4:
                return new Array<ISquareModel>(
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 5:
                return new Array<ISquareModel>(
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color)
                );
            case 6:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color)
                );
            case 7:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color)
                );
            case 8:
                return new Array<ISquareModel>(
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color)
                );
            case 9:
                return new Array<ISquareModel>(
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color),
                    this.fullSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            default: throw new Error(`${this} not implemented case ${rnd}`);
        }
    }

    private takeAndColorizePossible2x2(color: PieceColor): Array<ISquareModel> {
        const rnd = Math.round(Math.random() * (this.possible2x2Length - 1));
        switch (rnd) {
            case 0:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color),
                    this.emptySquareModelFactory.createSquare(null, null)
                );
            case 1:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color)
                );
            case 2:
                return new Array<ISquareModel>(
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 3:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color)
                );
            case 4:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 5:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.emptySquareModelFactory.createSquare(null, null)
                );
            case 6:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 7:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 8:
                return new Array<ISquareModel>(
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                )
            case 9:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 10:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.emptySquareModelFactory.createSquare(null, null)
                );
            case 11:
                return new Array<ISquareModel>(
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color)
                );
            case 12:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color)
                );
            case 13:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.emptySquareModelFactory.createSquare(null, null),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_RIGHT, color)
                );
            case 14:
                return new Array<ISquareModel>(
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_RIGHT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.BOTTOM_LEFT, color),
                    this.halfSquareModelFactory.createSquare(TriangleRotationStatus.TOP_LEFT, color),
                    this.emptySquareModelFactory.createSquare(null, null)
                );
            default: throw new Error(`${this} not implemented case ${rnd}`);
        }
    }
}